import CommonLookupDisplay from "components/CommonLookupDisplay";
import { GlobalContext } from "components/GlobalContext";
import { createFilterableArrayColumnDefinition, createFilterableNumberRangeColumnDefinition, IColumnDefinition } from "components/SortableTable";
import IDealer, { IDealerCreateOrUpdate, IDealerFilters, IDealerInitResult } from "models/sql/IDealer";
import { useContext, useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { applyGlobalFilterData } from "utilities/MiscUtilities";
import DealerService from "services/DealerService";
import useLocalStorage from "react-use-localstorage";
import { useNavigate, useSearchParams } from "react-router-dom";

interface IDealersLookupProps {
    baseData?: IDealerInitResult;
}

const COUNTRIES_FILTER_KEY = "countries";
const STATES_FILTER_KEY = "states";

const DealersLookup = (props: IDealersLookupProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [stickyLevel, setStickyLevel] = useLocalStorage("dealerStickyLevel", searchParams.get("dealerLevel") ?? undefined);
    const [filter, setFilter] = useState<IDealerFilters>({ dealerLevels: stickyLevel ? [stickyLevel] : undefined });
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const navigate = useNavigate();
    const levels = ["Channel Partner", "Tier 1", "Tier 2", "Tier 3", "Tier 4", "International"];

    useEffect(() => {
        if ((filter.dealerLevels?.join(",") || "") != stickyLevel) {
            setStickyLevel(filter.dealerLevels?.join(",") || "");
        }
    }, [filter]);

    useEffect(() => {
        if (stickyLevel) {
            setSearchParams({ ...searchParams, dealerLevel: stickyLevel || undefined });
            setFilter({ ...filter, dealerLevels: [stickyLevel] });
        }
        else {
            searchParams.delete("dealerLevel");
            setSearchParams({ ...searchParams });
            setFilter({ ...filter, dealerLevels: undefined });
        }
    }, [stickyLevel]);

    const exportTableDataAsync = async () => {
        const accessToken = await globalContext.getAccessTokenAsync();
        DealerService.DownloadExcelFileAsync(
            accessToken,
            "DealersExport.xlsx",
            applyGlobalFilterData(
                STATES_FILTER_KEY,
                COUNTRIES_FILTER_KEY,
                filter,
                globalContextRef.current.region,
                globalContextRef.current.organizationType));
    };

    const getColumns = (): IColumnDefinition<IDealer, IDealerFilters>[] => {
        return [
            createFilterableArrayColumnDefinition<IDealer, IDealerFilters>(
                globalContext,
                "dealerName",
                "dealerNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`dealerName${row.dealerId}`}>
                    {
                        (row.dealerName || "").split(",")
                            .map(
                                (e, index, array) => <>{e}{array.length > 1 && <br></br>}</>)
                    }</td>),
            createFilterableArrayColumnDefinition<IDealer, IDealerFilters>(
                globalContext,
                "emailSuffix",
                "emailSuffixes",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`emailSuffix${row.dealerId}`}>
                    {
                        (row.emailSuffix || "").split(",")
                            .map(
                                (e, index, array) => <>{e}{array.length > 1 && <br></br>}</>)
                    }</td>),
            createFilterableNumberRangeColumnDefinition<IDealer, IDealerFilters>(
                globalContext,
                "salesReps",
                // @ts-ignore
                "salesReps",
                true,
                filter,
                (value: any, row: any) => <td className="align-top" key={`salesReps${row.dealerId}`}>{row.salesReps?.length || 0}</td>,
                "Active Sales Reps"),
            {
                propName: "logoUrl",
                title: "HasLogo",
                sortable: true,
                customRenderer(value, row, allData) {
                    return <td className="align-top" key={`logoUrl${row.dealerId}`}>{value ? "Yes" : "No"}</td>;
                },
            },
        ];
    };

    const getTools = () => [
        <Form.Select
            style={{
                "display": "inline",
                "width": "130px",
                "position": "relative",
                "left": "-8px"
            }}
            className="float-start full-sm"
            onChange={async (e) => {
                setStickyLevel(e.target.value);
            }}
            value={stickyLevel}>
            <>
                <option value={""}>All Levels</option>
                {
                    levels.map((level, index) =>
                        <option key={index} value={level}>{level}</option>)
                }
            </>
        </Form.Select>,
        <Button
            className="me-2 full-sm"
            key="downloadExcel"
            onClick={() => { exportTableDataAsync(); }}>Export</Button>,
        <>
            {
                globalContext.roles.some(r => r === "Web.Admin") &&
                <Button
                    key="createnew"
                    variant="success"
                    className="full-sm"
                    onClick={() => { navigate(`/Dealers/Detail/`) }}>Create New</Button>
            }
        </>
    ];

    return (
        <CommonLookupDisplay<IDealerFilters, IDealer, IDealerCreateOrUpdate>
            baseFilters={{
                sortBys: ["DealerId"],
                sortDescending: true
            }}
            filterHints={{
                dealerIds: "Array",
                tftDealerIds: "Array",
                dealerNames: "Array",
                primarySalesContacts: "Array",
                primaryEmails: "Array",
                tftSalesmen: "Array",
                states: "Array",
                countries: "Array",
                zipcodes: "Array",
                sortBys: "Array",
                sortDescending: "Boolean",
                emailSuffixes: "Array"
            }}
            dataTypeName="Dealer"
            dataTypeNamePlural="Dealers"
            pathName="Dealers"
            primaryIdKey="dealerId"
            className=""
            countriesFilterKey={COUNTRIES_FILTER_KEY}
            statesFilterKey={STATES_FILTER_KEY}
            filter={filter}
            setFilter={setFilter}
            columnDefinitions={getColumns()}
            service={DealerService}
            tools={getTools()}
        />
    );
}

export default DealersLookup;
